/**
 * HoverZoom — a bigger look at a station image, for a MOUSE user only.
 *
 * A bonus on top of click-to-lightbox, which stays and is what touch uses. A
 * mouse resting on a preview pops the full-size image beside it; leaving,
 * pressing or scrolling closes it at once.
 *
 * NOT Radix HoverCard, deliberately. Its Trigger opens on focus, and a click
 * focuses the tile button. The shadcn wrapper does not portal, so the tile's
 * overflow-hidden frame would clip it. Its DismissableLayer registers document
 * listeners at mount and contends with a Radix Dialog. And it bridges hover
 * into the content, which this popup must never take.
 *
 * THE HOOK COSTS NOTHING UNTIL HOVERED: no listener, no matchMedia, no portal
 * and no fetch at mount. The mouse gate is asked on mouseenter only, a pointer
 * held still for 100ms prefetches the full image before the popup opens, the
 * scroll listener exists only while a popup is open, and hover state lives in
 * the hovered element, so a popup never re-renders the station around it. A
 * tile variant may separately warm full-size images in idle time (see
 * VariantStyle.warmFullSize in ProductionPreviewTile).
 *
 * Never takes focus (PRODUCTION_DESIGN §6.2): aria-hidden, pointer-events-none,
 * nothing focusable, and no preventDefault or stopPropagation.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ImgHTMLAttributes,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { warmImage } from '@/lib/image-thumbnail';
import { cn } from '@/lib/utils';

const EDGE_PX = 8;
const MAX_ZOOM_PX = 480;
/** "Only if clearly bigger": decided from the rendered rect, never a variant name. */
const MIN_ZOOM_SCALE = 1.5;
/** Inside the ~300ms budget, with room left for a cached image to paint. */
const OPEN_DELAY_MS = 200;
/** Half the open delay, counted from the pointer's last move. */
const PREFETCH_AFTER_MS = 100;
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';

/** The small label chip on a preview. Position is the caller's. */
export const PREVIEW_CHIP_CLASS =
  'rounded bg-background/85 px-1.5 py-0.5 text-sm font-medium leading-none text-foreground ring-1 ring-border';

interface ZoomBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * PURE. The popup box beside `rect`, on whichever side has more room, keeping
 * the element's own aspect and clamped to the viewport. Null when the zoom
 * would not be at least MIN_ZOOM_SCALE times the element.
 */
export function zoomGeometry(rect: ZoomBox, vw: number, vh: number): ZoomBox | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  const rightRoom = vw - rect.left - rect.width;
  const onRight = rightRoom >= rect.left;
  const room = onRight ? rightRoom : rect.left;
  const s = Math.min(
    Math.min(MAX_ZOOM_PX, room - 2 * EDGE_PX) / rect.width,
    Math.min(MAX_ZOOM_PX, vh - 2 * EDGE_PX) / rect.height,
  );
  if (s < MIN_ZOOM_SCALE) return null;
  const width = Math.round(rect.width * s);
  const height = Math.round(rect.height * s);
  return {
    left: onRight ? rect.left + rect.width + EDGE_PX : rect.left - EDGE_PX - width,
    top: Math.min(
      Math.max(rect.top + rect.height / 2 - height / 2, EDGE_PX),
      vh - EDGE_PX - height,
    ),
    width,
    height,
  };
}

/**
 * Hover props for the element to zoom, plus the popup node to render (null
 * while closed). `placeholderSrc` is the smaller copy already on screen when
 * `src` is its full-size twin: it is shown blown up until `src` loads, and it
 * is what makes a hover-intent prefetch worth doing.
 */
export function useHoverZoom(
  src: string | undefined,
  { label, placeholderSrc }: { label?: string; placeholderSrc?: string } = {},
) {
  const [open, setOpen] = useState<{ src: string; box: ZoomBox } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // True from a gated mouseenter until the open decision is made or it closes.
  const armed = useRef(false);
  // Open for THIS src only, so a tile that falls back closes its popup.
  const isOpen = open !== null && open.src === src;

  const close = useCallback(() => {
    armed.current = false;
    clearTimeout(timer.current);
    setOpen(null);
  }, []);

  useEffect(() => {
    if (!isOpen) return () => clearTimeout(timer.current);
    window.addEventListener('scroll', close, { capture: true, passive: true });
    return () => {
      clearTimeout(timer.current);
      window.removeEventListener('scroll', close, { capture: true });
    };
  }, [isOpen, close]);

  const hoverProps = useMemo(() => {
    // Every move restarts the chain, so the prefetch waits for the pointer to
    // rest PREFETCH_AFTER_MS and the popup opens OPEN_DELAY_MS after it
    // stopped. A pointer sweeping across a list never starts a download.
    const restart = (el: HTMLElement) => {
      if (!src) return;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        // Only when the zoom shows a different url than the one painted.
        if (placeholderSrc) warmImage(src);
        timer.current = setTimeout(() => {
          armed.current = false;
          const box = zoomGeometry(el.getBoundingClientRect(), window.innerWidth, window.innerHeight);
          if (box) setOpen({ src, box });
        }, OPEN_DELAY_MS - PREFETCH_AFTER_MS);
      }, PREFETCH_AFTER_MS);
    };
    return {
      onMouseEnter: (e: MouseEvent<HTMLElement>) => {
        armed.current = !!src && !!window.matchMedia?.(HOVER_QUERY).matches;
        if (armed.current) restart(e.currentTarget);
      },
      onMouseMove: (e: MouseEvent<HTMLElement>) => {
        if (armed.current) restart(e.currentTarget);
      },
      // Mouse events, not pointer events: a touch tap emits an emulated
      // mouseenter immediately followed by mousedown, which cancels the timer.
      onMouseLeave: close,
      onMouseDown: close,
    };
  }, [src, placeholderSrc, close]);

  const node = isOpen
    ? createPortal(
        <ZoomPopup
          src={open.src}
          box={open.box}
          label={label}
          placeholderSrc={placeholderSrc}
          onError={close}
        />,
        document.body,
      )
    : null;

  return { hoverProps, node };
}

const POPUP_IMG = 'absolute inset-0 h-full w-full object-contain';

function ZoomPopup({
  src,
  box,
  label,
  placeholderSrc,
  onError,
}: {
  src: string;
  box: ZoomBox;
  label?: string;
  placeholderSrc?: string;
  onError: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div
      data-hover-zoom
      aria-hidden="true"
      style={box}
      className={cn(
        'pointer-events-none fixed z-[60] overflow-hidden rounded-md border bg-background shadow-xl artwork-checker',
        // Never a blank box: shown once there is a picture to show.
        placeholderSrc || loaded ? 'opacity-100' : 'opacity-0',
      )}
    >
      {placeholderSrc && !loaded && <img src={placeholderSrc} alt="" className={POPUP_IMG} />}
      <img
        src={src}
        alt=""
        decoding="async"
        onLoad={(e) => {
          // load fires before a big image is decoded: swapping then flashes an empty box.
          const reveal = () => setLoaded(true);
          const img = e.currentTarget;
          if (typeof img.decode === 'function') img.decode().then(reveal, reveal);
          else reveal();
        }}
        onError={onError}
        className={cn(POPUP_IMG, loaded ? 'opacity-100' : 'opacity-0')}
      />
      {label && <span className={cn('absolute bottom-2 left-2', PREVIEW_CHIP_CLASS)}>{label}</span>}
    </div>
  );
}

/** Drop-in for a raw station img. Same DOM at rest: no wrapper element. */
export function ZoomImg(
  props: Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    'onMouseEnter' | 'onMouseMove' | 'onMouseLeave' | 'onMouseDown'
  >,
) {
  const zoom = useHoverZoom(props.src);
  return (
    <>
      <img {...props} {...zoom.hoverProps} />
      {zoom.node}
    </>
  );
}
